/**
 * LES PETITS MOTS QUI ACCOMPAGNENT UN NOM AFFICHE AU JOUEUR.
 *
 * Les messages fabriquaient leurs phrases a la main: « Cabane construit ! »,
 * « Descendre du voiture », « Le écureuil est apprivoisé », « Ce luciole a
 * déjà son ami ». Le jeu s'adresse a des enfants qui apprennent a ecrire, et
 * l'ecole du jeu est relue mot a mot par ses tests: le reste du jeu doit tenir
 * la meme exigence.
 *
 * Le GENRE est une donnee du modele (`feminin` sur BatimentDef, VehiculeDef,
 * AnimalDef), jamais devine d'apres la terminaison: « Musée » finit par -e et
 * il est masculin. Ce module ne fait que l'article, l'elision et l'accord.
 *
 * Module PUR, comme tout shared/sim.
 */

/** Ce qu'il faut savoir d'un nom pour l'accompagner. */
export interface Nom {
  /** Le nom tel qu'affiche (« Voiture », « Ours polaire »). */
  readonly nom: string
  readonly feminin?: boolean
}

/**
 * H ASPIRES du catalogue: on ne fait pas l'elision devant eux (« le hérisson »,
 * « la haie »). Tout autre h initial est muet (« l’hôtel »).
 */
const H_ASPIRES = ['hérisson', 'hibou', 'hamac', 'haie']

/** Vrai si l'article s'elide devant ce mot (voyelle ou h muet). */
export function elide(mot: string): boolean {
  const m = mot.toLowerCase()
  if (H_ASPIRES.some((h) => m.startsWith(h))) return false
  return /^[aàâeéèêëiîïoôuùûüyh]/.test(m)
}

const bas = (n: Nom): string => n.nom.toLowerCase()

/** « le lapin », « la luciole », « l’écureuil ». */
export function leNom(n: Nom): string {
  if (elide(n.nom)) return `l’${bas(n)}`
  return `${n.feminin ? 'la' : 'le'} ${bas(n)}`
}

/** Meme chose, en debut de phrase: « Le lapin », « L’écureuil ». */
export function LeNom(n: Nom): string {
  const s = leNom(n)
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** « du jet », « de la voiture », « de l’avion ». */
export function duNom(n: Nom): string {
  if (elide(n.nom)) return `de l’${bas(n)}`
  return n.feminin ? `de la ${bas(n)}` : `du ${bas(n)}`
}

/** « Ce lapin », « Cet écureuil », « Cette luciole » (debut de phrase). */
export function CeNom(n: Nom): string {
  if (n.feminin) return `Cette ${bas(n)}`
  return elide(n.nom) ? `Cet ${bas(n)}` : `Ce ${bas(n)}`
}

/** « cette école », « cet hôtel », « ce musée » (apres un verbe). */
export function ceNom(n: Nom): string {
  if (n.feminin) return `cette ${bas(n)}`
  return elide(n.nom) ? `cet ${bas(n)}` : `ce ${bas(n)}`
}

/** « Un lapin », « Une luciole » (debut de phrase). */
export function UnNom(n: Nom): string {
  return `${n.feminin ? 'Une' : 'Un'} ${bas(n)}`
}

/** « il » ou « elle ». */
export function pronom(n: Nom): string {
  return n.feminin ? 'elle' : 'il'
}

/** « Il » ou « Elle ». */
export function Pronom(n: Nom): string {
  return n.feminin ? 'Elle' : 'Il'
}

/** « le » ou « la » apres un verbe (« nourris-le », « nourris-la »). */
export function leComplement(n: Nom): string {
  return n.feminin ? 'la' : 'le'
}

/**
 * Accorde un adjectif ou un participe regulier: « construit » -> « construite »,
 * « prêt » -> « prête », « apprivoisé » -> « apprivoisée ».
 */
export function accorde(mot: string, n: Nom): string {
  return n.feminin ? `${mot}e` : mot
}
